import { defaultRegistry, makeModuleDataValidator, resolveModuleSection } from '../modules/registry.js';
import type { HeadContribution } from '../policy/index.js';
import {
  ContentSnapshot,
  SiteDefinition,
  resolveToContent,
  validateDefinition,
  validateSnapshotAgainstDefinition,
  type ContentSnapshotT,
  type SiteDefinitionT,
  type SplitIssue,
} from '../schema/split.js';
import { escapeHtml } from './escape.js';
import { renderSite, type RenderedSite } from './render.js';
import type { FirstPartyModule } from '../modules/kernel.js';

export interface RenderResolvedSiteOptions {
  /** Module registry. Defaults to the frozen first-party registry; an
   *  override exists for tests. Registries are CODE, not agent/editor input —
   *  no external data can introduce a module. */
  registry?: ReadonlyMap<string, FirstPartyModule>;
}

/** Render the definition's TYPED head contribution to markup. URLs are
 *  already validated by checkHeadContribution in validateDefinition; escaped
 *  here as defense in depth. There is no raw-string head input. */
function renderTypedHead(head: HeadContribution | undefined): string | undefined {
  if (!head) return undefined;
  const parts: string[] = [];
  for (const url of head.preconnect ?? []) parts.push(`<link rel="preconnect" href="${escapeHtml(url)}">`);
  for (const url of head.stylesheets ?? []) parts.push(`<link rel="stylesheet" href="${escapeHtml(url)}">`);
  return parts.length > 0 ? parts.join('\n') : undefined;
}

/** Thrown by renderResolvedSite when the definition/snapshot fail validation. */
export class ContentValidationError extends Error {
  readonly issues: SplitIssue[];
  constructor(issues: SplitIssue[]) {
    super(`content failed validation: ${issues.map((issue) => `${issue.where}: ${issue.message}`).join('; ')}`);
    this.name = 'ContentValidationError';
    this.issues = issues;
  }
}

/**
 * Render a Definition + Snapshot to a static site. This is the ONE mandatory
 * boundary: inputs are strictly parsed and fully validated (shape, definition
 * invariants, per-value content policy, module data) BEFORE anything renders.
 * Invalid content throws — it can never reach the renderer unvalidated.
 * (applyFreeformValues also sanitizes defensively, so safety does not hinge
 * on this call alone.)
 */
export function renderResolvedSite(
  definition: SiteDefinitionT,
  snapshot: ContentSnapshotT,
  options: RenderResolvedSiteOptions = {},
): RenderedSite {
  const { registry = defaultRegistry } = options;

  const parsedDef = SiteDefinition.safeParse(definition);
  if (!parsedDef.success) {
    throw new ContentValidationError(parsedDef.error.issues.map((issue) => ({ code: 'shape', where: issue.path.join('/'), message: issue.message })));
  }
  const parsedSnap = ContentSnapshot.safeParse(snapshot);
  if (!parsedSnap.success) {
    throw new ContentValidationError(parsedSnap.error.issues.map((issue) => ({ code: 'shape', where: issue.path.join('/'), message: issue.message })));
  }

  const issues = [
    ...validateDefinition(parsedDef.data),
    ...validateSnapshotAgainstDefinition(parsedDef.data, parsedSnap.data, makeModuleDataValidator(registry)),
  ];
  if (issues.length > 0) throw new ContentValidationError(issues);

  const content = resolveToContent(
    parsedDef.data,
    parsedSnap.data,
    (mount, moduleData) => resolveModuleSection(registry, mount, moduleData),
  );
  const extraHead = renderTypedHead(parsedDef.data.head);
  return renderSite(content, extraHead !== undefined ? { extraHead } : {});
}

/**
 * Parse a stored revision's definition + snapshot JSON and render it to files.
 * Throws ContentValidationError on invalid content (callers map it to their own
 * failure reason); a malformed-JSON error propagates as a generic Error.
 */
export function renderRevisionFiles(definitionJson: string, snapshotJson: string): Record<string, string> {
  const definition = JSON.parse(definitionJson) as SiteDefinitionT;
  const snapshot = JSON.parse(snapshotJson) as ContentSnapshotT;
  return renderResolvedSite(definition, snapshot).files;
}
